import {
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import WhatsAppIcon from '~/assets/icons/WhatsAppIcon';
import InstagramIcon from '~/assets/icons/InstagramIcon';
import GoogleDriveIcon from '~/assets/icons/GoogleDriveIcon';
import ShopifyIcon from '~/assets/icons/ShopifyIcon';

import {
  GET_DRIVE_INTEGRATION_URL,
  GET_INTEGRATIONS,
  UPDATE_CHANNEL_SETTINGS,
} from '~/services/integration';
import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useState, useMemo, useRef } from 'react';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';
import {
  ArrowPathIcon,
  ClipboardIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { REGISTER_META_TOKEN } from '~/services/meta';
import { GET_ADVANCE_SETTINGS } from '~/services';
import {
  GET_AGENT_DETAILS,
  GEN_SHOPIFY_INTEGRATION_CODE,
  UPDATE_SKIP_SHOPIFY,
} from '~/services/agents';
import {
  handleConnectInstagram,
  handleConnectWhatsapp,
  handleConnectGoogleDrive,
  handleDisconnectInstagram,
  handleGenericDisconnect,
} from './integration-handlers';

// Notification Banner Component
const NotificationBanner = ({
  message,
  type,
}: {
  message: string;
  type: 'success' | 'error';
}) => {
  return (
    <div
      className={`animate-notification mb-4 px-4 py-3 text-center font-medium ${
        type === 'success'
          ? 'border-b border-green-200 bg-green-100 text-green-700'
          : 'border-b border-red-200 bg-red-100 text-red-700' // Added mb-4, removed fixed positioning and z-index
      }`}
    >
      {type === 'success' ? '✅ ' : '❌ '}
      {message}
    </div>
  );
};

// Shopify Integration Modal Component
const ShopifyModal = ({
  isOpen,
  onClose,
  shopName,
  shopifyCode,
  business,
  isShopifyNotConnected,
  isIntegrationRefreshing,
  loadingGenCode,
  onCopyCode,
  onRefreshStatus,
  t,
}: {
  isOpen: boolean;
  onClose: () => void;
  shopName: string;
  shopifyCode: string;
  business: any;
  isShopifyNotConnected: boolean;
  isIntegrationRefreshing: boolean;
  loadingGenCode: boolean;
  onCopyCode: () => void;
  onRefreshStatus: () => void;
  t: any;
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (isOpen) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [isOpen]);

  const handleCloseModal = () => {
    onClose();
  };

  return (
    <dialog
      ref={dialogRef}
      onClose={handleCloseModal}
      className="modal"
      onClick={handleCloseModal}
    >
      <div
        className="modal-box flex max-w-2xl flex-col gap-4 rounded-lg bg-white p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-xl font-semibold text-gray-800">
            <ShopifyIcon className="h-6 w-6" />
            {t('dashboard:shopify.title') || 'Conectar con Tienda Shopify'}
          </h2>
          <button
            onClick={handleCloseModal}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-600">
          {t('dashboard:shopify.description') ||
            'Conecta tu agente con Shopify para habilitar el acceso al catálogo de productos y la gestión de pedidos.'}
        </p>

        {/* Content */}
        {business ? (
          <div className="space-y-4">
            {!isShopifyNotConnected && shopName ? (
              // Connected state - similar to AgentDashboard
              <div className="flex h-20 flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 px-4">
                <p className="flex items-center gap-2 text-lg">
                  {t('dashboard:shopify.connected_to', { shopName }) ||
                    `Conectado a ${shopName}`}
                  <CheckBadgeIcon className="h-6 w-6 text-green-500" />
                </p>
              </div>
            ) : (
              // Not connected state - similar to AgentDashboard layout
              <div className="space-y-4">
                {/* Install app link */}
                <a
                  className="block text-blue-500 underline"
                  href="https://apps.shopify.com/t-bit-sell-more-on-whatsapp"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('dashboard:shopify.install_app') ||
                    'Instalar nuestra app de Shopify'}
                </a>

                {/* Refresh button */}
                <div className="flex justify-end">
                  <button
                    className="cursor-pointer rounded p-2 hover:bg-gray-100"
                    disabled={loadingGenCode || isIntegrationRefreshing}
                    onClick={onRefreshStatus}
                    title={
                      t('dashboard:shopify.refresh_status') ||
                      'Actualizar estado de conexión'
                    }
                  >
                    <ArrowPathIcon
                      className={`h-5 w-5 ${
                        isIntegrationRefreshing ? 'animate-spin' : ''
                      }`}
                    />
                  </button>
                </div>

                {/* Code display - similar to AgentDashboard */}
                <div className="flex h-20 flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 px-4">
                  {shopifyCode &&
                  !loadingGenCode &&
                  !isIntegrationRefreshing ? (
                    <button
                      className="flex items-center justify-center gap-2 p-2 text-lg font-medium tracking-widest"
                      onClick={onCopyCode}
                      title={
                        t('dashboard:shopify.copy_code') ||
                        'Copiar código al portapapeles'
                      }
                    >
                      {shopifyCode}
                      <ClipboardIcon className="h-5 w-5" />
                    </button>
                  ) : (
                    <div className="flex items-center justify-center">
                      <ArrowPathIcon className="h-5 w-5 animate-spin text-gray-400" />
                      <span className="ml-2 text-sm text-gray-500">
                        Generando código...
                      </span>
                    </div>
                  )}
                </div>

                {/* Verify integration button */}
                <div className="flex justify-between">
                  <button
                    disabled={loadingGenCode || isIntegrationRefreshing}
                    onClick={onRefreshStatus}
                    className="btn btn-primary rounded-full"
                  >
                    {t('dashboard:shopify.verify_integration') ||
                      'Verificar integración'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
            <p className="text-sm text-yellow-800">
              {t('dashboard:shopify.no_business') ||
                'Este agente no tiene un perfil de negocio. La integración con Shopify no está disponible.'}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-4 flex justify-end gap-3">
          <button
            onClick={handleCloseModal}
            className="rounded-lg bg-gray-300 px-4 py-2 text-gray-700 transition-colors duration-200 hover:bg-gray-400"
          >
            Cerrar
          </button>
        </div>
      </div>
    </dialog>
  );
};

// Channel Settings Modal Component
const ChannelSettingsModal = ({
  isOpen,
  onClose,
  channel,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  channel: any | null;
  onSave: (payload: {
    channel_id: string;
    isEnabled: boolean;
    test_numbers: string[];
  }) => void;
}) => {
  const { t } = useTranslation('integration');
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [isEnabled, setIsEnabled] = useState<boolean>(true);
  const [numbersText, setNumbersText] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      dialogRef.current?.showModal();
      // Initialize state from channel
      if (channel) {
        setIsEnabled(!!channel.isEnabled);
        const list = Array.isArray(channel?.test_phone_numbers)
          ? channel.test_phone_numbers
          : [];
        setNumbersText(list.join(', '));
      }
    } else {
      dialogRef.current?.close();
    }
  }, [isOpen, channel]);

  const handleClose = () => onClose();
  const parseNumbers = (txt: string) =>
    txt
      .split(',')
      .map((n) => n.trim())
      .filter((n) => n.length > 0);

  const handleSave = () => {
    if (!channel?._id) return;
    onSave({
      channel_id: channel._id,
      isEnabled,
      test_numbers: parseNumbers(numbersText),
    });
  };

  const label =
    channel?.messaging_product === 'instagram' ? 'Instagram' : 'WhatsApp';
  const identifier =
    channel?.whatsapp_number || channel?.config?.instagram_name || '';

  return (
    <dialog
      ref={dialogRef}
      onClose={handleClose}
      className="modal"
      onClick={handleClose}
    >
      <div className="modal-box max-w-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            {t('channel_settings.title', { label })}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <div className="text-sm text-gray-500">
              {t('channel_settings.identifier')}
            </div>
            <div className="text-base font-medium">{identifier || '-'}</div>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              className="toggle toggle-success"
              checked={isEnabled}
              onChange={(e) => setIsEnabled(e.target.checked)}
            />
            <span className="text-sm">{t('channel_settings.enabled')}</span>
            <span
              className={`ml-2 inline-block h-2 w-2 rounded-full ${
                isEnabled ? 'bg-green-500' : 'bg-red-500'
              }`}
              title={
                isEnabled
                  ? t('channel_settings.online')
                  : t('channel_settings.offline')
              }
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">
              {t('channel_settings.test_numbers_label')}
            </label>
            <input
              className="input input-bordered w-full"
              placeholder={t('channel_settings.test_numbers_placeholder')}
              value={numbersText}
              onChange={(e) => setNumbersText(e.target.value)}
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('channel_settings.test_numbers_helper')}
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button className="btn" onClick={handleClose}>
            {t('channel_settings.cancel')}
          </button>
          <button className="btn btn-primary" onClick={handleSave}>
            {t('channel_settings.save')}
          </button>
        </div>
      </div>
    </dialog>
  );
};

const IntegrationsScreen = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('integration');
  const { agentId } = useParams();
  const [searchParams] = useSearchParams();
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [registerToken] = useMutation(REGISTER_META_TOKEN);
  const [showAllState, setShowAllState] = useState<{ [key: string]: boolean }>(
    {},
  );

  // Shopify integration states
  const [business, setBusiness] = useState<any>(null);
  const [shopName, setShopName] = useState('');
  const [isShopifyNotConnected, setIsShopifyNotConnected] = useState(true);
  const [showShopifyModal, setShowShopifyModal] = useState(false);
  const [shopifyCode, setShopifyCode] = useState('');
  const [isIntegrationRefreshing, setIsIntegrationRefreshing] = useState(false);

  const { data, refetch } = useQuery(GET_ADVANCE_SETTINGS, {
    variables: {
      data: {
        agent_id: agentId,
      },
    },
  });

  // Channels state for settings modal
  const channelsList = useMemo(
    () => data?.get_advance_settings?.data?.channels || [],
    [data],
  );
  const [channelDialogOpen, setChannelDialogOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<any | null>(null);
  const [updateChannelSettings, { loading: updatingChannel }] = useMutation(
    UPDATE_CHANNEL_SETTINGS,
    {
      onCompleted: () => {
        setNotification({
          type: 'success',
          message: 'Channel settings updated',
        });
        refetch(); // refresh channels list
      },
      onError: (error) => {
        console.error('Error updating channel settings:', error);
        setNotification({
          type: 'error',
          message: 'Error updating channel settings',
        });
      },
    },
  );

  const openChannelDialog = (channel: any) => {
    setSelectedChannel(channel);
    setChannelDialogOpen(true);
  };
  const closeChannelDialog = () => {
    setChannelDialogOpen(false);
    setSelectedChannel(null);
  };
  const saveChannelDialog = (payload: {
    channel_id: string;
    isEnabled: boolean;
    test_numbers: string[];
  }) => {
    updateChannelSettings({ variables: { data: payload } }).finally(() => {
      setChannelDialogOpen(false);
    });
  };

  // Get agent details for Shopify business information
  const { data: agentData, refetch: refetchAgent } = useQuery(
    GET_AGENT_DETAILS,
    {
      variables: {
        data: {
          agent_id: agentId,
        },
      },
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        if (data.response?.data?.business) {
          setBusiness(data.response.data.business);
          setShopName(data.response.data.business.shopify?.shop_name || '');
          setIsShopifyNotConnected(
            !data.response.data.business.shopify?.shop_name,
          );
        }
      },
    },
  );

  // Generate Shopify integration code
  const [genCode, { loading: loadingGenCode }] = useMutation(
    GEN_SHOPIFY_INTEGRATION_CODE,
    {
      onCompleted: (data) => {
        if (data.response?.data?.shopify_code) {
          setShopifyCode(data.response.data.shopify_code);
          setShowShopifyModal(true);
        }
      },
      onError: (error) => {
        console.error('Error generating Shopify code:', error);
        setNotification({
          message: 'Error generating Shopify integration code',
          type: 'error',
        });
      },
    },
  );

  const serviceIcons: { [key: string]: React.ComponentType<any> } = {
    whatsapp: WhatsAppIcon,
    instagram: InstagramIcon,
    googleDrive: GoogleDriveIcon,
    shopify: ShopifyIcon,
  };

  const { data: integrationsData, loading: integrationsLoading } = useQuery(
    GET_INTEGRATIONS,
    {
      variables: {
        data: {
          agent_id: agentId,
        },
      },
      fetchPolicy: 'network-only', // Don't use cache, always make a new request
    },
  );

  const allIntegrationsData = useMemo(() => {
    return i18n.getResourceBundle(i18n.language, 'integration');
  }, [i18n, i18n.language]);

  const topLevelKeysToExclude = [
    'title',
    'integration_description',
    'connect',
    'disconnect',
  ];

  const categoryIntegrationKeys: string[] = useMemo(() => {
    if (!allIntegrationsData) {
      return [];
    }
    return Object.keys(allIntegrationsData).filter((key) => {
      if (topLevelKeysToExclude.includes(key)) {
        return false;
      }
      const item = allIntegrationsData[key];
      // Ensure item is an object and has a 'title' property, typical for categories
      return (
        typeof item === 'object' &&
        item !== null &&
        'title' in item &&
        Object.keys(item).some(
          (subKey) =>
            typeof item[subKey] === 'object' &&
            item[subKey] !== null &&
            'title' in item[subKey],
        )
      ); // Check if category has actual service items
    });
  }, [allIntegrationsData, topLevelKeysToExclude]); // Added topLevelKeysToExclude to dependencies

  const [
    getDriveIntegrationUrl,
    { loading: driveUrlLoading, data: driveUrlData, error: driveUrlError },
  ] = useLazyQuery(GET_DRIVE_INTEGRATION_URL, {
    onCompleted: (data) => {
      if (data && data.get_drive_integration_url.data) {
        window.location.href = data.get_drive_integration_url.data; // Redirige en la misma pestaña
      }
    },
    onError: (error) => {
      console.error('Error fetching Google Drive integration URL:', error);
    },
  });

  useEffect(() => {
    const status = searchParams.get('status');
    const service = searchParams.get('service');

    if (status && service) {
      // Ensure allIntegrationsData is loaded before proceeding
      if (!allIntegrationsData) return;

      // Special handling for Shopify success - refresh agent data
      if (service === 'shopify' && status === 'success') {
        refetchAgent();
      }

      // Find the category that contains this service
      const categoryKey = categoryIntegrationKeys.find((catKey) => {
        const category = allIntegrationsData[catKey];
        return (
          category &&
          typeof category === 'object' &&
          Object.prototype.hasOwnProperty.call(category, service)
        );
      });

      if (categoryKey) {
        if (status === 'success') {
          const message = t(
            `${categoryKey}.${service}.success`,
            `${service} connected successfully!`,
          );
          console.log(message);
          setNotification({ message, type: 'success' });
        } else if (status === 'error') {
          const message = t(
            `${categoryKey}.${service}.error`,
            `Error connecting to ${service}`,
          );
          console.error(message);
          setNotification({ message, type: 'error' });
        }
      } else {
        // Fallback if category not found
        if (status === 'success') {
          const message = `${service} connected successfully!`;
          setNotification({ message, type: 'success' });
        } else if (status === 'error') {
          const message = `Error connecting to ${service}`;
          console.error(message);
          setNotification({ message, type: 'error' });
        }
      }
    }
  }, [
    searchParams,
    categoryIntegrationKeys,
    allIntegrationsData,
    t,
    i18n.language,
    refetchAgent,
  ]); // Added refetchAgent to dependencies

  // Auto dismiss notification after 4 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000); // Changed from 4000 to 5000

      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Generate Shopify code when modal is opened and code doesn't exist
  useEffect(() => {
    if (showShopifyModal && business && business._id && !shopifyCode) {
      genCode({
        variables: {
          input: {
            business_id: business._id,
          },
        },
      });
    }
  }, [showShopifyModal, business, shopifyCode]);

  // Handle Shopify connection
  const handleConnectShopify = () => {
    if (!business?._id) {
      setNotification({
        message:
          'Business profile not found. Please wait for business data to load or try refreshing the page.',
        type: 'error',
      });
      return;
    }

    // Open the modal - code should already be generated from useEffect
    setShowShopifyModal(true);
  };

  // Copy code to clipboard
  const copyCodeToClipboard = () => {
    navigator.clipboard.writeText(shopifyCode);
    setNotification({
      type: 'success',
      message:
        t('dashboard:shopify.code_copied') || 'Code copied to clipboard!',
    });
  };

  // Refresh business status and check integration
  const refreshBusinessStatus = () => {
    setIsIntegrationRefreshing(true);
    refetchAgent().finally(() => {
      setIsIntegrationRefreshing(false);
    });
  };

  const handleConnectClick = (
    categoryKey: string,
    serviceKey: string,
    isConnected: boolean,
  ) => {
    if (isConnected) {
      // Handle disconnect logic based on service type
      if (serviceKey === 'instagram') {
        // Use the handler function for Instagram disconnect
        handleDisconnectInstagram(
          agentId,
          refetch,
          t(`${categoryKey}.${serviceKey}.disconnect`) || t('disconnect'),
        );
      } else if (serviceKey === 'shopify') {
        // For Shopify, we don't have a direct disconnect - it's managed through business settings
        console.log(
          'Shopify disconnect should be handled through business settings',
        );
        setNotification({
          message:
            'Shopify disconnect should be managed through your business settings.',
          type: 'error',
        });
      } else {
        // Use the generic disconnect handler for other services
        handleGenericDisconnect(agentId, serviceKey);
      }
    } else {
      // Connect logic
      console.log(`Connecting ${serviceKey} for agent ${agentId}...`);
      if (serviceKey === 'googleDrive') {
        handleConnectGoogleDrive(agentId, getDriveIntegrationUrl);
      } else if (serviceKey === 'whatsapp') {
        handleConnectWhatsapp(agentId, registerToken, navigate, refetch);
      } else if (serviceKey === 'instagram') {
        handleConnectInstagram(agentId);
        console.log(
          `Attempting to connect ${serviceKey} for agent ${agentId}...`,
        );
      } else if (serviceKey === 'shopify') {
        handleConnectShopify();
      }
    }
  };

  if (integrationsLoading) {
    return <p className="text-center text-gray-500">Loading integrations...</p>;
  }

  const enabledIntegrations = integrationsData?.get_integrations?.data || [];

  function integrationNameConverter(str: string) {
    return str.toLowerCase().replace('_', '');
  }

  return (
    <>
      {notification && (
        <NotificationBanner
          message={notification.message}
          type={notification.type}
        />
      )}

      {!allIntegrationsData ? (
        <p className="text-center text-gray-500">Loading integration data...</p>
      ) : categoryIntegrationKeys.length > 0 ? (
        categoryIntegrationKeys.map((categoryKey) => {
          const category = allIntegrationsData[categoryKey];
          if (!category || typeof category !== 'object') return null;

          const serviceKeys = Object.keys(category).filter((serviceKey) => {
            if (serviceKey === 'title' || serviceKey === 'description') {
              return false;
            }
            const serviceItem = category[serviceKey];
            return (
              typeof serviceItem === 'object' &&
              serviceItem !== null &&
              'title' in serviceItem
            );
          });

          return (
            <div key={categoryKey} className="mb-10">
              <h2 className="text-base-content mb-1 text-xl font-semibold">
                {t(`${categoryKey}.title`)}
              </h2>
              <p className="text-base-content mb-5 text-sm">
                {t(`${categoryKey}.description`)}
              </p>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {serviceKeys.map((serviceKey) => {
                  const IconComponent = serviceIcons[serviceKey];

                  // Special handling for Shopify connection status
                  let isConnected = false;
                  let serviceIntegrations: any[] = [];

                  if (serviceKey === 'shopify') {
                    // Check Shopify connection through business data
                    isConnected = !isShopifyNotConnected && !!shopName;
                    if (isConnected) {
                      serviceIntegrations = [
                        {
                          integration_name: 'shopify',
                          identifier: shopName,
                        },
                      ];
                    }
                  } else {
                    // Get all integrations for this service type
                    serviceIntegrations = enabledIntegrations.filter(
                      (integration: {
                        integration_name: string;
                        identifier: string;
                      }) =>
                        integrationNameConverter(
                          integration.integration_name,
                        ) === serviceKey.toLowerCase(),
                    );
                    isConnected = serviceIntegrations.length > 0;
                  }

                  const showAll = showAllState[serviceKey] || false;
                  const visibleIntegrations = showAll
                    ? serviceIntegrations
                    : serviceIntegrations.slice(0, 3);

                  return (
                    <div
                      key={serviceKey}
                      className="border-base-300 bg-base-100 flex flex-col justify-between rounded-lg border p-4 shadow-sm transition-shadow duration-200 ease-in-out hover:shadow-lg"
                    >
                      <div>
                        <div className="mb-2 flex items-center gap-3">
                          {IconComponent && (
                            <IconComponent className="h-6 w-6" />
                          )}
                          <h3 className="text-md text-base-content font-semibold">
                            {t(`${categoryKey}.${serviceKey}.title`)}
                          </h3>
                        </div>
                        <p className="text-base-content mb-4 mt-1 flex-grow text-xs">
                          {t(`${categoryKey}.${serviceKey}.description`)}
                        </p>
                      </div>

                      {/* Show all connected integrations for this service */}
                      {isConnected && (
                        <div className="mb-3">
                          {visibleIntegrations.map(
                            (integration: any, index: number) => {
                              // Find matching channel by identifier and product
                              const product =
                                integration?.integration_name?.toLowerCase?.() ||
                                '';
                              const channel = channelsList.find((c: any) => {
                                if (c.messaging_product !== product)
                                  return false;
                                if (product === 'whatsapp')
                                  return (
                                    c.whatsapp_number === integration.identifier
                                  );
                                if (product === 'instagram')
                                  return (
                                    c?.config?.instagram_name ===
                                    integration.identifier
                                  );
                                return false;
                              });
                              const enabled = channel?.isEnabled !== false;
                              return (
                                <button
                                  type="button"
                                  key={index}
                                  className={`mb-2 flex w-full items-center justify-center gap-2 rounded-full border px-3 py-2 text-center text-sm font-black ${
                                    enabled
                                      ? 'border-green-200 bg-green-50 text-green-600'
                                      : 'border-red-200 bg-red-50 text-red-600'
                                  }`}
                                  title={t(
                                    'channel_settings.edit_channel_settings',
                                  )}
                                  onClick={() =>
                                    channel && openChannelDialog(channel)
                                  }
                                >
                                  <span
                                    className={`inline-block h-2 w-2 rounded-full ${
                                      enabled ? 'bg-green-500' : 'bg-red-500'
                                    }`}
                                  />
                                  <CheckBadgeIcon
                                    className={`h-4 w-4 ${
                                      enabled
                                        ? 'text-green-500'
                                        : 'text-red-500'
                                    }`}
                                  />
                                  {t('connected')}
                                  {integration.identifier && (
                                    <span
                                      className={`${
                                        enabled
                                          ? 'text-green-700'
                                          : 'text-red-700'
                                      } ml-1 text-xs`}
                                    >
                                      {integration.identifier}
                                    </span>
                                  )}
                                </button>
                              );
                            },
                          )}

                          {/* Show more/less button if there are more than 3 integrations */}
                          {serviceIntegrations.length > 3 && (
                            <button
                              className="mb-2 w-full text-center text-xs text-blue-600 hover:text-blue-800"
                              onClick={() =>
                                setShowAllState((prev) => ({
                                  ...prev,
                                  [serviceKey]: !prev[serviceKey],
                                }))
                              }
                            >
                              {showAll
                                ? t('dashboard:connected_channels.show_less')
                                : t('dashboard:connected_channels.show_more', {
                                    count: serviceIntegrations.length - 3,
                                  })}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Action buttons */}
                      {isConnected ? (
                        <div className="space-y-2">
                          {/* Add another integration button */}
                          {serviceKey !== 'shopify' && (
                            <button
                              className="btn btn-secondary w-full rounded-full px-3 py-2 text-sm font-black focus:outline-none focus:ring-2"
                              onClick={() =>
                                handleConnectClick(
                                  categoryKey,
                                  serviceKey,
                                  false,
                                )
                              }
                            >
                              {serviceKey === 'whatsapp'
                                ? t('dashboard:add_another_whatsapp')
                                : serviceKey === 'instagram'
                                  ? t('dashboard:add_another_instagram')
                                  : t('connect')}
                            </button>
                          )}

                          {/* Disconnect button if available */}
                          {allIntegrationsData[categoryKey]?.[serviceKey]
                            ?.disconnect && (
                            <button
                              className="btn btn-primary w-full rounded-full rounded-full px-3 py-2 text-sm font-black focus:outline-none focus:ring-2"
                              onClick={() =>
                                handleConnectClick(
                                  categoryKey,
                                  serviceKey,
                                  isConnected,
                                )
                              }
                            >
                              {t(`${categoryKey}.${serviceKey}.disconnect`) ||
                                t('disconnect')}
                            </button>
                          )}
                        </div>
                      ) : (
                        <button
                          className={`btn btn-primary mt-auto w-full rounded-full rounded-full px-3 py-2 text-sm font-black focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                          onClick={() =>
                            handleConnectClick(
                              categoryKey,
                              serviceKey,
                              isConnected,
                            )
                          }
                        >
                          {t('connect')}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      ) : (
        <p className="text-center text-gray-500">No integrations available.</p>
      )}

      <p className="text-xs">
        {t('connection.disclaimer')}
        <a
          href="https://www.t-bit.io/documentos/terminos_y_condiciones"
          target="blank"
          className="ml-1 font-semibold text-blue-400"
        >
          {t('connection.terms')}
        </a>
      </p>

      <ShopifyModal
        isOpen={showShopifyModal}
        onClose={() => setShowShopifyModal(false)}
        shopName={shopName}
        shopifyCode={shopifyCode}
        business={business}
        isShopifyNotConnected={isShopifyNotConnected}
        isIntegrationRefreshing={isIntegrationRefreshing}
        loadingGenCode={loadingGenCode}
        onCopyCode={copyCodeToClipboard}
        onRefreshStatus={refreshBusinessStatus}
        t={t}
      />

      <ChannelSettingsModal
        isOpen={channelDialogOpen}
        onClose={closeChannelDialog}
        channel={selectedChannel}
        onSave={saveChannelDialog}
      />
    </>
  );
};

export default IntegrationsScreen;
